import cloneDeep from 'lodash/cloneDeep';

import {
    ChoiceQuestion,
    EntityDict,
    Group,
    isChoiceQuestion,
    isPageOrderGroup,
    PageOrder,
    Question,
    Widget,
    QuestionOption,
    Revision,
    RevisionState,
    OptionNode,
    QuestionNode,
    LogicNode,
    TableChoiceQuestion,
    TableQuestion,
    ExtendedQuestionType,
    isTableQuestion,
    WithLogic
} from 'client/common/types';

export const EMOTION_OPTION_COUNTS = [2, 3, 5];

export const RATING_MAX_VALUES = [2, 3, 4, 5, 6, 7, 8, 9, 10];

export const SCALE_MIN_VALUES = [0, 1];
export const SCALE_MAX_VALUES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export const CHOICE_MIN_OPTION_COUNT = 2;
export const TABLE_MIN_OPTION_COUNT = 2;
export const TABLE_MIN_COLUMN_COUNT = 2;
export const TABLE_MIN_ROW_COUNT = 2;

export const DEFAULT_TIME_LIMIT = 5;

export function canRemoveOption(question: ChoiceQuestion): boolean {
    const isSpecialOptionEnabled = [
        question.noAnswer.enabled,
        question.noOpinionAnswer.enabled,
        question.otherAnswer.enabled,
        question.otherAnswerWithText.enabled
    ].some(Boolean);

    const limit = isSpecialOptionEnabled ? CHOICE_MIN_OPTION_COUNT - 1 : CHOICE_MIN_OPTION_COUNT;

    return question.options.length > limit;
}

export function canRemoveSpecialOption(question: Question | Widget): boolean {
    if (isChoiceQuestion(question) && !canRemoveOption(question)) {
        return (
            [
                question.noAnswer.enabled,
                question.noOpinionAnswer.enabled,
                question.otherAnswer.enabled,
                question.otherAnswerWithText.enabled
            ].filter(Boolean).length > 1
        );
    }

    return true;
}

export function canRemoveColumn(question: TableChoiceQuestion): boolean {
    const isSpecialOptionEnabled = [
        question.noAnswer.enabled,
        question.noOpinionAnswer.enabled,
        question.otherAnswer.enabled,
        question.otherAnswerWithText.enabled
    ].some(Boolean);

    const limit = isSpecialOptionEnabled ? TABLE_MIN_COLUMN_COUNT - 1 : TABLE_MIN_COLUMN_COUNT;

    return question.columns.length > limit;
}

export function canRemoveRow(question: TableQuestion): boolean {
    return question.rows.length > TABLE_MIN_ROW_COUNT;
}

export function isChoiceQuestionRestricted(question: ChoiceQuestion): boolean {
    return question.multipleChoice && Boolean(question.maxAnswerCount);
}

export function getOption(
    questionId: Question['id'],
    optionId: QuestionOption['id'],
    questions: EntityDict<Question>
): QuestionOption | undefined {
    const question = questions[questionId];

    if (!isChoiceQuestion(question)) {
        return;
    }

    return question.options.find(({id}) => id === optionId);
}

export function getLogicElement(node: LogicNode, revision: Revision): WithLogic | undefined {
    switch (node.type) {
        case 'question': {
            return revision.questions[node.id];
        }
        case 'option': {
            return getOption(node.questionId, node.id, revision.questions);
        }
        case 'widget': {
            return revision.widgets[node.id];
        }
        case 'page': {
            return revision.groups[node.id];
        }
    }
}

export function getFlatPageOrder(pageOrder: PageOrder): Group['id'][] {
    return pageOrder.reduce((acc: Group['id'][], item) => {
        if (isPageOrderGroup(item)) {
            acc = acc.concat(item.pages);
        } else {
            acc.push(item);
        }

        return acc;
    }, []);
}

export function getActiveRevision<T extends Pick<Revision, 'state'>>(
    revisions: T[]
): T | undefined {
    return revisions.find(({state}) => state === RevisionState.ACTIVE);
}

export function getCurrentRevision<T extends Pick<Revision, 'state'>>(revisions: T[]): T {
    return revisions[0];
}

export function isTemplate(q: Question | Widget) {
    return Boolean(q.isTemplate);
}

export function isDisabled(q: Question | Widget) {
    return {
        value: Boolean(q.isDisabled),
        canChange: typeof q.isDisabled === 'boolean'
    };
}

export function getParentQuestionId(node: QuestionNode | OptionNode) {
    let pageId;

    switch (node.type) {
        case 'question':
            pageId = node.id;
            break;
        case 'option':
            pageId = node.questionId;
            break;
    }

    return pageId;
}

export function getParentPageId(node: LogicNode, groups: EntityDict<Group>) {
    if (node.type === 'page') {
        return node.id;
    }
    if (node.type === 'widget') {
        const result = Object.keys(groups).find((groupNumber: string) =>
            groups[groupNumber].children.some(
                groupChild => groupChild.id === node.id && groupChild.type === node.type
            )
        );

        return Number(result);
    }

    for (const pageNum of Object.keys(groups)) {
        if (!groups[pageNum].children.length) {
            return;
        }

        const question = groups[pageNum].children.find(
            child => child.id === getParentQuestionId(node)
        );

        if (question) {
            return Number(pageNum);
        }
    }

    return;
}

export function findIndexOfRotationGroup(pageOrder: PageOrder | undefined, pageId: number) {
    if (!pageOrder) return -1;

    const indexOfElementToModify = pageOrder.findIndex(page => {
        if (isPageOrderGroup(page) && page.pages.includes(pageId)) {
            return true;
        }

        return false;
    });

    return indexOfElementToModify;
}

const getRotationGroups = (pageOrder: PageOrder) => {
    return pageOrder.reduce((acc: Group['id'][][], group) => {
        if (isPageOrderGroup(group)) {
            acc.push([...group.pages]);
        }

        return acc;
    }, []);
};

export function getNewPageOrder(initialPageOrder: PageOrder, modifiedFlatPageOrder: number[]) {
    let newPageOrder: PageOrder = cloneDeep(modifiedFlatPageOrder);
    const groups = getRotationGroups(initialPageOrder);

    for (const group of groups) {
        const rotationGroupStartingPageIndex = newPageOrder.indexOf(group[0]);

        const firstHalf = newPageOrder.slice(0, rotationGroupStartingPageIndex);
        const secondHalf = newPageOrder.slice(rotationGroupStartingPageIndex + group.length);

        newPageOrder = [...firstHalf, {rotation: true, pages: group}, ...secondHalf];
    }

    return newPageOrder;
}

export function isSameType(questionType: Question['type'], newType: ExtendedQuestionType) {
    if (questionType === newType) return true;
    if (questionType === 'choice' && newType === 'multichoice') return true;
    if (questionType === 'table' && newType === 'multitable') return true;
    if (questionType === 'number' && newType === 'percent') return true;

    return false;
}

export function getQuestionIdsNotBound(questions: Question[]) {
    const allBoundQuestions = questions.reduce<number[]>((acc, question) => {
        if (isTableQuestion(question)) {
            acc.push(...question.boundQuestions);
        }

        return acc;
    }, []);

    return Object.keys(questions).filter(id => !allBoundQuestions.includes(Number(id)));
}
